import { useAccountContext } from '@/context/account'
import { useClaimRewardsTransactions } from '@/hooks/useClaimRewardsTransactions'
import { useClaimRewardsQuery } from '@/queries/claimRewards.query'
import type { Rewards } from '@/queries/rewards.query'
import type { SdkNetwork } from '@/types/network'
import { cn } from '@/utils/cn'
import { getNetworkName } from '@/utils/network/getNetworkName'
import { pluralize } from '@/utils/pluralize'
import { formatPreciseTokenValue, formatUSD } from '@vaultsfyi/common/utils'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button } from '../common/button'
import { Checkbox } from '../common/checkbox'
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogContentWrapper,
  DialogHeader,
  DialogHeaderWrapper,
  DialogScrollArea,
  DialogTitle,
  DialogTrigger,
} from '../common/dialog'
import { Label } from '../common/label'
import { NetworkImage } from '../common/networkImage'
import { Note } from '../common/note'
import { Separator } from '../common/separator'
import { TokenImage } from '../common/tokenImage'
import { CircleCheckIcon } from '../icons/check'
import { LoadingIcon } from '../icons/loading'
import { StatusMessage } from '../shared/statusMessage'

interface Props {
  rewards: NonNullable<Rewards>
}

interface NetworkTxState {
  status: 'idle' | 'loading' | 'success' | 'error'
  error?: string
}

export const RewardsDialog = ({ rewards }: Props) => {
  const { account } = useAccountContext()
  const [txState, setTxState] = useState<'idle' | 'loading' | 'success'>('idle')
  const [networkTxStates, setNetworkTxStates] = useState<Record<string, NetworkTxState>>({})
  const getAllClaimIds = useCallback(
    (rewards: NonNullable<Rewards>): Set<string> => {
      const claimIds = new Set<string>()

      for (const networkRewards of Object.values(rewards)) {
        networkRewards?.forEach((reward) => {
          claimIds.add(reward.claimId)
        })
      }

      return claimIds
    },
    [rewards]
  )

  const allClaimIds = useMemo(() => getAllClaimIds(rewards), [rewards])

  const [deselectedRewardKeys, setDeselectedRewardKeys] = useState<Set<string>>(new Set())

  const selectedRewardKeys = useMemo(() => {
    const selected = new Set(allClaimIds)
    deselectedRewardKeys.forEach((id) => {
      if (allClaimIds.has(id)) {
        selected.delete(id)
      }
    })
    return selected
  }, [allClaimIds, deselectedRewardKeys])

  const selectedClaimIds = useMemo(() => Array.from(selectedRewardKeys), [selectedRewardKeys])

  const handleGroupToggle = useCallback(
    (network: SdkNetwork, checked: boolean) => {
      setDeselectedRewardKeys((prev) => {
        const newSet = new Set(prev)
        const networkRewards = rewards[network]

        if (!networkRewards) return newSet

        if (checked) {
          networkRewards.forEach((reward) => newSet.delete(reward.claimId))
        } else {
          networkRewards.forEach((reward) => newSet.add(reward.claimId))
        }

        return newSet
      })
    },
    [rewards]
  )

  const handleItemToggle = useCallback((rewardKey: string, checked: boolean) => {
    setDeselectedRewardKeys((prev) => {
      const newSet = new Set(prev)

      if (checked) {
        newSet.delete(rewardKey)
      } else {
        newSet.add(rewardKey)
      }

      return newSet
    })
  }, [])

  const { claimActions, isFetching } = useClaimRewardsQuery({ address: account, claimIds: selectedClaimIds })
  const isClaimDisabled = selectedClaimIds.length === 0 || isFetching

  const [txsCount, setTxsCount] = useState(0)

  const setTransactionsFinished = useCallback(() => {
    setTxState('success')
  }, [setTxState])

  const setNetworkTxLoading = useCallback((network: string) => {
    setNetworkTxStates((prev) => ({
      ...prev,
      [network]: { status: 'loading' },
    }))
  }, [])

  const setNetworkTxSuccess = useCallback((network: string) => {
    setNetworkTxStates((prev) => ({
      ...prev,
      [network]: { status: 'success' },
    }))
  }, [])

  const setNetworkTxError = useCallback((network: string, error: string) => {
    setNetworkTxStates((prev) => ({
      ...prev,
      [network]: { status: 'error', error },
    }))
  }, [])

  const { getTxsCount, executeTxs } = useClaimRewardsTransactions({
    claimActions,
    setTransactionsFinished,
    setNetworkTxLoading,
    setNetworkTxSuccess,
    setNetworkTxError,
  })

  useEffect(() => {
    let cancelled = false
    getTxsCount().then((count) => {
      if (!cancelled) {
        setTxsCount(count)
      }
    })
    return () => {
      cancelled = true
    }
  }, [getTxsCount])

  const handleClaimRewards = useCallback(async () => {
    setTxState('loading')
    setNetworkTxStates({})
    try {
      await executeTxs()
      setTxState('success')
    } catch (error) {
      console.error('Error executing claim transactions:', error)
      setTxState('idle')
    }
  }, [executeTxs])

  const handleCloseModal = useCallback(() => {
    setTxState('idle')
    setNetworkTxStates({})
  }, [])

  const isSubmitDisabled = isClaimDisabled || txState === 'loading'

  const completedTxsCount = Object.values(networkTxStates).filter((state) => state.status === 'success').length
  const totalNetworksCount = Object.keys(networkTxStates).length
  const failedTxsCount = Object.values(networkTxStates).filter((state) => state.status === 'error').length

  const networkErrors = Object.entries(networkTxStates)
    .filter(([_, state]) => state.status === 'error')
    .map(([network, state]) => ({ network, error: state.error }))

  const errorContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (networkErrors.length > 0 && errorContainerRef.current) {
      errorContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }, [networkErrors.length])

  return (
    <Dialog onOpenChange={handleCloseModal} modal>
      <DialogTrigger asChild>
        <Button size="lg" className="w-full shadow-button-primary-inner">
          Claim rewards
        </Button>
      </DialogTrigger>

      <DialogContent>
        <DialogBody>
          <DialogHeaderWrapper>
            <DialogHeader>
              <DialogTitle>{txState === 'success' ? 'Rewards claimed' : 'Claimable rewards'}</DialogTitle>
            </DialogHeader>
            <Separator />
          </DialogHeaderWrapper>
          {txState === 'success' ? (
            <DialogScrollArea className="max-h-72">
              <DialogContentWrapper className="pt-4">
                <StatusMessage>
                  <CircleCheckIcon size={48} className="zoom-in fade-in mx-auto animate-in text-accent" />
                  {failedTxsCount > 0 ? (
                    <>
                      <h6>
                        {completedTxsCount} of {pluralize(totalNetworksCount, 'claim')} succeeded
                      </h6>
                      <p className="text-heading-foreground text-sm">
                        {failedTxsCount} {pluralize(failedTxsCount, 'claim')} failed
                      </p>
                    </>
                  ) : (
                    <h6>Rewards claimed successfully</h6>
                  )}
                </StatusMessage>
              </DialogContentWrapper>
            </DialogScrollArea>
          ) : (
            <DialogScrollArea className="max-h-72">
              <DialogContentWrapper className="!pt-0 !pb-0">
                <div className="flex flex-col gap-4">
                  <NetworkList
                    rewards={rewards}
                    selectedRewardKeys={selectedRewardKeys}
                    onGroupToggle={handleGroupToggle}
                    onItemToggle={handleItemToggle}
                    networkTxStates={networkTxStates}
                    txState={txState}
                  />
                  {networkErrors.length > 0 && (
                    <div ref={errorContainerRef} className="flex flex-col gap-1">
                      {networkErrors.map(({ network }) => (
                        <Note color="red" key={network}>
                          <p>
                            Claiming rewards transaction on{' '}
                            <span className="capitalize">{getNetworkName(network)}</span> failed. Try again.
                          </p>
                        </Note>
                      ))}
                    </div>
                  )}
                </div>
              </DialogContentWrapper>
            </DialogScrollArea>
          )}
          <DialogContentWrapper className="!pt-0">
            <Separator />
            {txState === 'success' ? (
              <DialogClose asChild>
                <Button variant="secondary" size="md" className="w-full">
                  Close
                </Button>
              </DialogClose>
            ) : (
              <Button size="lg" className="flex w-full gap-2" disabled={isSubmitDisabled} onClick={handleClaimRewards}>
                {isFetching ? (
                  <>
                    Loading rewards <LoadingIcon size={16} className="animate-spin" />
                  </>
                ) : txState === 'loading' ? (
                  <>
                    Claiming rewards {txsCount > 1 && `${completedTxsCount}/${txsCount}`}{' '}
                    <LoadingIcon size={16} className="animate-spin" />
                  </>
                ) : (
                  <>Claim rewards {txsCount > 1 && `(${txsCount} transactions)`}</>
                )}
              </Button>
            )}
          </DialogContentWrapper>
        </DialogBody>
      </DialogContent>
    </Dialog>
  )
}

interface NetworkListProps {
  rewards: NonNullable<Rewards>
  selectedRewardKeys: Set<string>
  onGroupToggle: (network: SdkNetwork, checked: boolean) => void
  onItemToggle: (rewardKey: string, checked: boolean) => void
  networkTxStates: Record<string, NetworkTxState>
  txState: 'idle' | 'loading' | 'success'
}

const NetworkList = ({
  rewards,
  selectedRewardKeys,
  onGroupToggle,
  onItemToggle,
  networkTxStates,
  txState,
}: NetworkListProps) => (
  <ul>
    {Object.entries(rewards).map(([network, networkRewards]) => {
      const networkKey = network as SdkNetwork
      const isGroupSelected = networkRewards?.every((reward) => selectedRewardKeys.has(reward.claimId))
      const networkTxState = networkTxStates[network]

      return (
        <NetworkGroup
          key={network}
          network={networkKey}
          rewards={networkRewards}
          isSelected={isGroupSelected}
          onGroupToggle={onGroupToggle}
          onItemToggle={onItemToggle}
          selectedRewardKeys={selectedRewardKeys}
          networkTxState={networkTxState}
          txState={txState}
        />
      )
    })}
  </ul>
)

interface NetworkGroupProps {
  network: SdkNetwork
  rewards: NonNullable<Rewards>[SdkNetwork]
  isSelected: boolean | undefined
  onGroupToggle: (network: SdkNetwork, checked: boolean) => void
  onItemToggle: (rewardKey: string, checked: boolean) => void
  selectedRewardKeys: Set<string>
  networkTxState?: NetworkTxState
  txState: 'idle' | 'loading' | 'success'
}

const NetworkGroup = ({
  network,
  rewards,
  isSelected,
  onGroupToggle,
  onItemToggle,
  selectedRewardKeys,
  networkTxState,
  txState,
}: NetworkGroupProps) => (
  <li className="border-b last:border-b-0">
    <Label className="flex items-center gap-2 py-2">
      <Checkbox
        checked={isSelected}
        onCheckedChange={(checked) => onGroupToggle(network, !!checked)}
        disabled={txState === 'loading'}
      />
      <NetworkImage network={network} className="size-5 p-0" />
      <p className="capitalize">{getNetworkName(network)}</p>
      {networkTxState?.status === 'loading' ? (
        <LoadingIcon size={16} className="ml-0.5 animate-spin" />
      ) : networkTxState?.status === 'success' ? (
        <CircleCheckIcon size={16} className="text-success" />
      ) : null}
    </Label>

    <ul>
      {rewards?.map((reward) => (
        <RewardItem
          key={reward.claimId}
          reward={reward}
          isSelected={selectedRewardKeys.has(reward.claimId)}
          onToggle={onItemToggle}
          txState={txState}
        />
      ))}
    </ul>
  </li>
)

interface RewardItemProps {
  reward: NonNullable<NonNullable<Rewards>[SdkNetwork]>[number]
  isSelected: boolean
  onToggle: (rewardKey: string, checked: boolean) => void
  txState: 'idle' | 'loading' | 'success'
}

const RewardItem = ({ reward, isSelected, onToggle, txState }: RewardItemProps) => (
  <li>
    <Label
      className={cn(
        'flex items-center justify-between gap-2 pt-1 pb-2 pl-7 text-heading-foreground',
        'border-b last:border-b-0'
      )}
    >
      <div className="flex items-center gap-2">
        <Checkbox
          checked={isSelected}
          onCheckedChange={(checked) => onToggle(reward.claimId, !!checked)}
          disabled={txState === 'loading'}
        />
        <span>${reward.asset.symbol}</span>
      </div>

      <div className="flex items-center gap-2">
        <p className="font-semibold">
          {reward.asset.claimableAmountInUsd
            ? formatUSD(reward.asset.claimableAmountInUsd, { notation: 'standard' })
            : formatPreciseTokenValue({
                value: reward.asset.claimableAmount,
                decimals: reward.asset.decimals,
              })}
        </p>
        <TokenImage symbol={reward.asset.symbol} className="size-5 p-0" />
      </div>
    </Label>
  </li>
)
